<template>
  <div class="student-management-panel">
    <div class="student-management-group-table-container d-flex flex-column">
      <v-row
        v-if="showStudentManagementTestConnectionBtn"
        id="testConnectionContainer"
        align-content="center"
        align="center"
        no-gutters
      >
        <v-col cols="auto">
          <cs-button
            id="btnTestConnection"
            primary
            class="ml-2"
            name="btnTestConnection"
            :disabled="studentManagementTestConnectionInProgress"
            :loading="studentManagementTestConnectionInProgress"
            label="Test Connection"
            @click="studentManagementTestConnection()"
          ></cs-button>
        </v-col>
        <v-col self-align="left" class="pt-1">
          <v-label
            :class="
              studentManagementTestConnectionFailedStatus.length > 0 ? 'generic-error-message' : ''
            "
          >
            {{ studentManagementTestConnectionStatusDisplay }}
          </v-label>
        </v-col>
      </v-row>
      <v-row class="d-flex justify-start">
        <v-col>
          <cs-button
            id="btnClientRetrieveGroupsFromVendor"
            :primary="!(isReadFromCache && !groupsLoading)"
            name="btnClientRetrieveGroupsFromVendor"
            :loading="groupsLoading"
            label="Retrieve Groups from Vendor"
            @click="updateGroups(false)"
          ></cs-button>
          <cs-button
            id="btnClientRetrieveGroupsFromCache"
            primary
            name="btnClientRetrieveGroupsFromCache"
            label="Retrieve Groups from Cache"
            :disabled="!isReadFromCache || groupsLoading"
            @click="updateGroups(true)"
          ></cs-button>
        </v-col>
      </v-row>
      <v-row no-gutters class="pl-1" align="center">
        <v-col>
          <v-autocomplete
            ref="selectGroup"
            v-model="selectedApplyParentId"
            class="student-management-panel-parent-select"
            :items="originalGroupList"
            placeholder="Set parent for selected group"
            item-title="group"
            item-value="id"
            clearable
            :multiple="false"
            hide-details
            hide-selected
            variant="solo"
            density="compact"
            color="primary"
          >
            <template #clear>
              <v-icon color="primary" @click="clearApplyParentSelection">mdi-close</v-icon>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col class="pt-3">
          <v-checkbox
            id="client-individual-apply-subject-checkbox"
            v-model="applySubjectEnabled"
            label="Subject"
            hide-details
            density="compact"
            color="primary"
          ></v-checkbox>
        </v-col>
        <v-col class="pt-3">
          <v-checkbox
            id="client-individual-apply-custom-checkbox"
            v-model="applyCustomEnabled"
            label="Custom"
            hide-details
            density="compact"
            color="primary"
          ></v-checkbox>
        </v-col>
        <v-col>
          <cs-button
            id="btnApply"
            primary
            name="btnApply"
            :disabled="applyButtonDisabled()"
            label="Apply"
            @click="applySelectedOption()"
          ></cs-button>
        </v-col>
        <v-spacer></v-spacer>
        <v-col class="d-flex justify-end">
          <cs-button
            id="btnGroupsSave"
            primary
            name="btnGroupsSave"
            label="Save"
            :disabled="saveButtonDisabled()"
            @click="confirmSaveDialogDisplayed = true"
          ></cs-button>
        </v-col>
      </v-row>
    </div>
    <v-row v-if="studentManagementGroupSyncDiffs.length > 0">
      <v-col>
        <student-group-sync-diff-alert
          :student-group-sync-diffs="studentManagementGroupSyncDiffs"
        />
      </v-col>
    </v-row>
    <v-row class="student-management-group-search-container">
      <v-col col="8">
        <v-text-field
          id="student-management-search-input"
          v-model="searchTerm"
          single-line
          clearable
          class="ml-2"
          placeholder="Search Group Name"
          variant="underlined"
          color="primary"
          @click:clear="clearSearchInput()"
        >
          <template #clear>
            <v-icon color="primary" @click="clearSearchInput()">mdi-close</v-icon>
          </template>
        </v-text-field>
      </v-col>
      <v-col class="d-flex justify-start pt-1">
        <cs-button
          id="btnStudentManagementPanelSearch"
          primary
          name="btnStudentGroupSearch"
          label="Search Group"
          @click="filterGroups()"
        ></cs-button>
      </v-col>
    </v-row>

    <div style="overflow: auto; height: 90vh">
      <v-data-table
        v-model="selectedGroupItems"
        v-model:options="studentGroupPaginationInfo"
        :headers="groupHeaders"
        :items="groupList"
        class="elevation-2 student-management-table"
        :loading="tableLoadingBar"
        no-data-text="No group was found"
        :items-per-page-options="footerProps['items-per-page-options']"
        show-select
        return-object
      >
        <template #item="{ item }">
          <tr>
            <td class="row-select-checkbox">
              <v-checkbox
                :model-value="selectedGroupItems"
                :value="item"
                color="primary"
                multiple
                hide-details
                @update:model-value="clickCheckbox"
              />
            </td>
            <td class="text-left">
              {{ item.group }}
              <span v-if="vendorGroupIdDisplayEnabled" class="studentGroupId">
                [{{ item.id }}]</span
              >
            </td>
            <td>
              <v-select
                :id="getStudentManagementSchoolTypeSelectionId(item.group)"
                v-model="item.school"
                clearable
                class="type-dropdown student-management-panel-student-select"
                :items="schoolTypes"
                item-title="name"
                item-value="id"
                placeholder="School type"
                variant="solo"
                density="compact"
                :hide-details="true"
                color="primary"
              >
                <template #clear>
                  <v-icon color="primary" @click="clearSchoolTypeSelection(item)">mdi-close</v-icon>
                </template>
              </v-select>
            </td>
            <td class="text-left">
              <div v-if="!item.parentSelectionDisplayed">
                <a
                  :id="getStudentManagementParentSelectionId(item.group)"
                  class="student-management-panel-parent"
                  @click="displayParentSelection(item)"
                >
                  {{
                    !item.parent
                      ? '[select]'
                      : getParentGroupName(item.parent).length === 0
                        ? '[missing parent]'
                        : getParentGroupName(item.parent)
                  }}
                </a>
                <span v-if="vendorGroupIdDisplayEnabled && item.parent" class="studentGroupId">
                  [{{ getParentGroupId(item.parent) }}]</span
                >
              </div>
              <v-autocomplete
                v-if="item.parentSelectionDisplayed"
                v-model="item.parent"
                class="student-management-panel-student-select"
                :items="originalGroupList"
                placeholder="Parent"
                item-title="group"
                item-value="id"
                clearable
                :multiple="false"
                variant="solo"
                density="compact"
                :hide-details="true"
                color="primary"
                @blur="parentSelected(item)"
              >
                <template #clear="{ props: clearProps }">
                  <v-icon color="primary" @click="clearParentSelection(item, clearProps)"
                    >mdi-close</v-icon
                  >
                </template>
              </v-autocomplete>
            </td>
            <td class="text-left">
              <v-checkbox-btn v-model="item.subjectGroup" color="primary"></v-checkbox-btn>
            </td>
            <td class="text-left">
              <v-checkbox-btn v-model="item.customGroup" color="primary"></v-checkbox-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
    <cs-form-dialog
      :model-value="groupSaveGenericDialogDisplayed"
      name="CSDialog"
      max-width="490"
      heading="Student Management"
      body-id="group-import-modal--generic-dialog"
      :primary-action="{ label: 'OK', eventName: 'primary-click' }"
      persistent
      @primary-click="genericDialogOkBtnClick"
      @update:model-value="genericDialogCloseBtnClick"
    >
      <template #cs-form-dialog-content>
        <v-spacer></v-spacer>
        <div :class="isGenericDialogDisplayedAsError ? 'generic-error-message' : ''">
          <div v-safe-html="groupSaveGenericDialogMessage"></div>
        </div>
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      id="confirm-save-form-dialog"
      v-model="confirmSaveDialogDisplayed"
      heading="Student Management"
      :primary-action="{ label: 'Continue', eventName: 'primary-click' }"
      @primary-click="saveButtonClick"
    >
      <template #cs-form-dialog-content>
        <div>
          <div v-if="studentManagementGroupSyncDiffs.length > 0">
            <student-group-sync-diff-alert
              :student-group-sync-diffs="studentManagementGroupSyncDiffs"
              :display-ignore-message="true"
            />
          </div>
          <span>You are about to save the group mappings. Are you sure you want to proceed?</span>
        </div>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import * as types from '@/store/mutationTypes';
import { CSBase } from '@complispace/cs-design-system';
import errorCaseSid from '../lib/const/errorCaseSid';
import studentGroupTableColumn from '../lib/const/studentGroupTableColumn';
import { populateErrorMessage } from '../lib/errorMessages';
import studentManagement from '../lib/studentManagement';
import dbEntityRelation from '../lib/const/dbEntityRelation';
import StudentGroupSyncDiffAlert from './StudentGroupSyncDiffAlert';

export default {
  name: 'StudentManagementPanel',

  components: {
    StudentGroupSyncDiffAlert
  },

  extends: CSBase,

  data() {
    return {
      showStudentManagementTestConnectionBtn: false,
      studentManagementTestConnectionInProgress: false,
      studentManagementTestConnectionAt: '',
      studentManagementTestConnectionFailedStatus: '',
      isGenericDialogDisplayedAsError: false,
      isGenericDialogDisplayedReloadAfter: true,
      footerProps: {
        'items-per-page-options': [10, 30, 50]
      },
      studentGroupPaginationInfo: {
        itemsPerPage: 30,
        page: 1,
        sortBy: []
      },
      schoolTypes: [],
      savedSchoolTypes: [],
      schoolTypeValid: true,
      groupHeaders: [
        {
          title: studentGroupTableColumn.GROUP.label,
          key: studentGroupTableColumn.GROUP.tableColumnRef,
          headerProps: { class: 'table-header' },
          width: 200
        },
        {
          title: studentGroupTableColumn.SCHOOL_TYPE.label,
          key: studentGroupTableColumn.SCHOOL_TYPE.tableColumnRef,
          headerProps: { class: 'table-header' },
          width: 200
        },
        {
          title: studentGroupTableColumn.PARENT.label,
          key: studentGroupTableColumn.PARENT.tableColumnRef,
          headerProps: { class: 'table-header' },
          width: 200
        },
        {
          title: studentGroupTableColumn.SUBJECT_GROUP.label,
          key: studentGroupTableColumn.SUBJECT_GROUP.tableColumnRef,
          headerProps: { class: 'table-header' },
          align: 'start',
          width: 110
        },
        {
          title: studentGroupTableColumn.CUSTOM_GROUP.label,
          key: studentGroupTableColumn.CUSTOM_GROUP.tableColumnRef,
          headerProps: { class: 'table-header' },
          align: 'center',
          width: 110
        }
      ],
      selectedGroupItems: [],
      groupList: [],
      groupsLoading: false,
      selectedApplyParentId: null,
      applySubjectEnabled: false,
      applyCustomEnabled: false,
      clientStudentGroupSubsetAttributeKeys: ['sub_long', 'sub_short', 'sub_type'],
      componentKey: 0,
      groupSaveGenericDialogDisplayed: false,
      groupSaveGenericDialogMessage: '',
      groupSaveGenericDialogSuccessMessage: 'Groups have been saved.',
      querystringRefToTableColumnRefMap: {},
      tableColumnRefToQuerystringRefMap: {},
      searchTerm: '',
      originalGroupList: [],
      savedGroupManagement: {},
      studentManagementGroupSyncDiffs: [],
      confirmSaveDialogDisplayed: false,
      isReadFromCache: false
    };
  },
  computed: {
    tableLoadingBar() {
      return this.groupsLoading ? 'primary' : false;
    },
    vendorGroupIdDisplayEnabled() {
      return this.$store.state.common.vendorGroupIdDisplayEnabled;
    },
    studentManagementTestConnectionButtonDisabled() {
      return this.studentManagementTestConnectionInProgress;
    },
    studentManagementTestConnectionStatusDisplay() {
      if (
        this.studentManagementTestConnectionFailedStatus.length === 0 &&
        this.studentManagementTestConnectionAt.length > 0
      ) {
        return `Last Succeed At: ${this.$app
          .moment(this.studentManagementTestConnectionAt)
          .format('YYYY-MM-DD HH:mm:ss')}.`;
      }

      if (this.studentManagementTestConnectionAt.length > 0) {
        return `Last Failed At: ${this.$app
          .moment(this.studentManagementTestConnectionAt)
          .format('YYYY-MM-DD HH:mm:ss')}. ${this.studentManagementTestConnectionFailedStatus}`;
      }

      return '';
    }
  },
  watch: {
    studentGroupPaginationInfo: {
      handler(val) {
        this.getStudentGroupFilterData(val);
      },
      deep: true
    },
    '$route.query': {
      immediate: true,
      handler(newVal, oldVal) {
        if (Object.keys(newVal).length === 0 && oldVal && Object.keys(oldVal).length !== 0) {
          window.location.reload();
        }
      }
    },
    searchTerm() {
      this.resetStudentGroupPaginationInfo();
    }
  },
  async mounted() {
    const { query } = this.$route;
    Object.keys(studentGroupTableColumn).forEach((key) => {
      this.querystringRefToTableColumnRefMap[studentGroupTableColumn[key].querystringRef] =
        studentGroupTableColumn[key].tableColumnRef;
      this.tableColumnRefToQuerystringRefMap[studentGroupTableColumn[key].tableColumnRef] =
        studentGroupTableColumn[key].querystringRef;
    });
    const { axiosInstance, eventPluginCommonAdapter, stbApiAdapter } = this.$app;
    const isVendorAuthScheme =
      !!this.$route.query['vendor-sid'] && !!this.$route.query['oauth-signature'];
    if (!isVendorAuthScheme) {
      await this.$store.dispatch('authorisation/validateAndSetUserTokenOrLogout', {
        query: this.$route.query,
        axiosInstance
      });
    }
    await this.$store.dispatch('common/initClientConfig', {
      queryParam: isVendorAuthScheme ? this.$route.query : {},
      eventPluginCommonAdapter,
      stbApiAdapter
    });
    const { data } = await this.$app.stbApiAdapter.getClientStudentGroupManagements({
      store: this.$store
    });
    const entryHash = JSON.parse(JSON.stringify(data.entryHash));
    const associationHash = JSON.parse(JSON.stringify(data.associationHash));
    if (Object.keys(entryHash) !== 0) {
      await this.populateGroupTable(entryHash, associationHash);
    }
    this.updateQueryString(query);
  },
  methods: {
    getStudentManagementParentSelectionId(group) {
      return `student-management-panel--parent--selection_${group}`;
    },
    getStudentManagementSchoolTypeSelectionId(group) {
      return `student-management-panel--school-type--selection_${group}`;
    },
    async updateGroups(readFromCache) {
      this.clearSearchInput();

      const requestParam = {
        'vendor-sid': this.$store.state.common.apiParam['vendor-sid'],
        'oauth-signature': this.$store.state.common.apiParam['oauth-signature']
      };

      const getClientsArgHash = {
        sid: this.$store.state.common.userClientSid,
        requestParam,
        store: this.$store
      };
      this.groupsLoading = true;
      const cbGetClientsResponse = await this.$app.stbApiAdapter.getClients(getClientsArgHash);
      if (!cbGetClientsResponse.error) {
        const schoolTypeData = JSON.parse(
          JSON.stringify(cbGetClientsResponse.data[0].metadata.schoolType)
        );
        this.schoolTypes = [];
        if (schoolTypeData) {
          Object.keys(schoolTypeData).forEach((schoolKey) => {
            this.schoolTypes.push({
              id: schoolKey,
              name: schoolTypeData[schoolKey]
            });
          });
        }
      }

      const cbResponse = await this.$app.stbApiAdapter.getClientStudentGroupListLite(readFromCache);
      this.groupsLoading = false;
      if (!cbResponse.error) {
        this.isReadFromCache = true;
        const remoteGroupList = cbResponse.data;
        const existingGroupList = JSON.parse(JSON.stringify(this.groupList));
        const existingGroupManagement = { ...this.savedGroupManagement };

        this.studentManagementGroupSyncDiffs = studentManagement.getGroupSyncDiffs(
          existingGroupManagement,
          remoteGroupList
        );

        this.groupList = remoteGroupList.map((remoteGroup) => {
          const existingGroupDetected = existingGroupList.find(
            (s) => `${s.id}` === `${remoteGroup.id}`
          );

          if (existingGroupDetected && existingGroupDetected.group !== remoteGroup.name) {
            existingGroupDetected.group = remoteGroup.name;
          }

          const resolvedGroup = existingGroupDetected
            ? JSON.parse(JSON.stringify(existingGroupDetected))
            : {
                id: remoteGroup.id,
                group: remoteGroup.name
              };

          this.clientStudentGroupSubsetAttributeKeys.forEach((subsetAttributeKey) => {
            const subsetAttributeValue = remoteGroup[subsetAttributeKey];

            if (typeof subsetAttributeValue !== 'undefined') {
              resolvedGroup[subsetAttributeKey] = subsetAttributeValue;
            }
          });

          return resolvedGroup;
        });

        this.originalGroupList = this.$app.lodash.cloneDeep(this.groupList);
      } else if (errorCaseSid.VENDOR_TOKEN_KEY_INVALID === `${cbResponse.errorCaseSid}`) {
        // @TOREVIEW: this.studentManagementClientApiFields is not defined,
        // no element of clientEditForm existed, these code shall be removed
        const apiEndpointField = this.studentManagementClientApiFields.filter(
          (item) => item.label === 'Token Key'
        )[0];
        const errors = [];
        errors.push(`Invalid Token Key`);
        apiEndpointField.errorMessage = errors;
        document.getElementsByClassName('clientEditForm')[0].scrollTop = 200;
        this.$forceUpdate();
      } else if (errorCaseSid.VENDOR_API_CREDENTIALS_INVALID === `${cbResponse.errorCaseSid}`) {
        const tokenKeyField = this.studentManagementClientApiFields.filter(
          (item) => item.label === 'Token Key'
        )[0];
        const errors = [];
        errors.push(this.invalidCredentials);
        tokenKeyField.errorMessage = errors;
        document.getElementsByClassName('clientEditForm')[0].scrollTop = 200;
        this.$forceUpdate();
      } else if (errorCaseSid.VENDOR_API_KEY_INVALID === `${cbResponse.errorCaseSid}`) {
        const apiKeyField = this.studentManagementClientApiFields.filter(
          (item) => item.label === 'API Key'
        )[0];
        const errors = [];
        errors.push(`Invalid API key`);
        apiKeyField.errorMessage = errors;
        this.$forceUpdate();
      } else if (errorCaseSid.VENDOR_ORGANIZATION_ID_INVALID === `${cbResponse.errorCaseSid}`) {
        const organizationIdField = this.studentManagementClientApiFields.filter(
          (item) => item.label === 'Organization ID'
        )[0];
        const errors = [];
        errors.push(`Invalid Organization ID`);
        organizationIdField.errorMessage = errors;
        this.$forceUpdate();
      } else if (errorCaseSid.VENDOR_API_ENDPOINT_INVALID === `${cbResponse.errorCaseSid}`) {
        const apiEndpointField = this.studentManagementClientApiFields.filter(
          (item) => item.label === 'API Endpoint'
        )[0];
        const errors = [];
        errors.push(`Invalid API Endpoint`);
        apiEndpointField.errorMessage = errors;
        document.getElementsByClassName('clientEditForm')[0].scrollTop = 0;
        this.$forceUpdate();
      } else {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);

        this.groupSaveGenericDialogDisplayed = true;
        this.isGenericDialogDisplayedReloadAfter = false;
        this.isGenericDialogDisplayedAsError = true;
        this.groupSaveGenericDialogMessage = cbResponse.message.replace(
          /: ses/,
          '<br/><br/>Sentinel Event ID: ses'
        );
      }
    },
    parentSelected(item) {
      const localItem = item;
      localItem.parentSelectionDisplayed = false;
      this.$forceUpdate();
    },
    getParentGroupName(parentId) {
      const parentGroup = this.originalGroupList.find((g) => `${g.id}` === `${parentId}`);
      return parentGroup ? parentGroup.group : '';
    },
    getParentGroupId(parentId) {
      const parentGroup = this.originalGroupList.find((g) => `${g.id}` === `${parentId}`);
      return parentGroup ? parentGroup.id : '';
    },
    displayParentSelection(item) {
      /* eslint-disable no-param-reassign */
      this.groupList.forEach((g) => {
        g.parentSelectionDisplayed = false;
        if (g.id === item.id) {
          g.parentSelectionDisplayed = true;
        }
      });
      /* eslint-enable no-param-reassign */
      this.groupList = this.$app.lodash.cloneDeep(this.groupList);
      this.$forceUpdate();
    },
    applyButtonDisabled() {
      return !this.selectedGroupItems || this.selectedGroupItems.length === 0;
    },
    applySelectedOption() {
      const { selectedApplyParentId } = this;
      if (this.selectedGroupItems && this.selectedGroupItems.length > 0) {
        this.selectedGroupItems.forEach((group) => {
          const localGroup = group;
          if (this.applySubjectEnabled) {
            localGroup.subjectGroup = true;
          }
          if (this.applyCustomEnabled) {
            localGroup.customGroup = true;
          }
          localGroup.parent = selectedApplyParentId;
        });
      }
      this.selectedGroupItems = [];
      this.selectedApplyParentId = null;
      this.applySubjectEnabled = false;
      this.applyCustomEnabled = false;
      this.$forceUpdate();
    },
    saveButtonDisabled() {
      return this.groupList.length === 0;
    },
    async saveButtonClick() {
      this.confirmSaveDialogDisplayed = false;
      const groupManagement = this.constructStudentManagementGroupManagement();
      const argHash = { payload: groupManagement, store: this.$store };

      const cbResponse = await this.$app.stbApiAdapter.putClientStudentGroupManagements(argHash);
      if (!cbResponse.error) {
        if (
          this.groupList.length !== this.originalGroupList &&
          this.searchTerm &&
          String(this.searchTerm).trim().length !== 0
        ) {
          const parsedSearchTerm = String(this.searchTerm).trim().toLowerCase();
          const leftOriginalGroupList = this.originalGroupList.filter(
            (item) => !String(item.group).toLowerCase().includes(parsedSearchTerm)
          );
          const updatedGroupList = leftOriginalGroupList.concat(this.groupList);
          this.originalGroupList = this.$app.lodash.cloneDeep(updatedGroupList);
        }
        this.groupSaveGenericDialogMessage = this.groupSaveGenericDialogSuccessMessage;
        this.groupSaveGenericDialogDisplayed = true;
      } else {
        let dialogMessage = 'Group cannot be saved.';

        if (cbResponse.refinedMessage) {
          dialogMessage = `${dialogMessage} ${cbResponse.refinedMessage}.`;
        }

        this.groupSaveGenericDialogMessage = populateErrorMessage({
          message: dialogMessage,
          store: this.$store
        });
        this.groupSaveGenericDialogDisplayed = true;
      }
      this.clearSearchInput();
    },
    async genericDialogOkBtnClick() {
      this.groupSaveGenericDialogDisplayed = false;
      this.isGenericDialogDisplayedAsError = false;
      if (this.isGenericDialogDisplayedReloadAfter) {
        this.$app.window.location.reload();
      } else {
        this.isGenericDialogDisplayedReloadAfter = true;
      }
    },
    async genericDialogCloseBtnClick(boolShow) {
      if (!boolShow) {
        this.groupSaveGenericDialogDisplayed = false;
        this.isGenericDialogDisplayedAsError = false;
        if (this.isGenericDialogDisplayedReloadAfter) {
          this.$app.window.location.reload();
        } else {
          this.isGenericDialogDisplayedReloadAfter = true;
        }
      }
    },
    clickCheckbox(selected) {
      this.selectedGroupItems = selected;
    },
    constructStudentManagementGroupManagement() {
      const groupManagement = {
        entryHash: {},
        associationHash: {
          year: {},
          class: {},
          custom: {},
          subject: {}
        }
      };

      if (
        this.groupList.length !== this.originalGroupList &&
        this.searchTerm &&
        String(this.searchTerm).trim().length !== 0
      ) {
        const parsedSearchTerm = String(this.searchTerm).trim().toLowerCase();
        const leftOriginalGroupList = this.originalGroupList.filter(
          (item) => !String(item.group).toLowerCase().includes(parsedSearchTerm)
        );
        const updatedGroupList = leftOriginalGroupList.concat(this.groupList);
        updatedGroupList.forEach((clientStudentGroup) => {
          const entryId = clientStudentGroup.id;
          const entry = {
            name: clientStudentGroup.group,
            parentId: '',
            parentType: ''
          };
          groupManagement.entryHash[entryId] = entry;

          this.clientStudentGroupSubsetAttributeKeys.forEach((subsetAttributeKey) => {
            const subsetAttributeValue = clientStudentGroup[subsetAttributeKey];

            if (typeof subsetAttributeValue !== 'undefined') {
              entry[subsetAttributeKey] = subsetAttributeValue;
            }
          });

          if (clientStudentGroup.school) {
            entry.parentId = `${clientStudentGroup.school}`;
            entry.parentType = 'school';
            groupManagement.associationHash.year[entryId] = true;
          } else if (clientStudentGroup.parent) {
            entry.parentId = `${clientStudentGroup.parent}`;
            entry.parentType = 'group';
            if (clientStudentGroup.subjectGroup) {
              groupManagement.associationHash.subject[entryId] = true;
            } else {
              groupManagement.associationHash.class[entryId] = true;
            }
          } else if (clientStudentGroup.customGroup) {
            groupManagement.associationHash.custom[entryId] = true;
          }
        });
      } else {
        this.groupList.forEach((clientStudentGroup) => {
          const entryId = clientStudentGroup.id;
          const entry = {
            name: clientStudentGroup.group,
            parentId: '',
            parentType: ''
          };
          groupManagement.entryHash[entryId] = entry;

          this.clientStudentGroupSubsetAttributeKeys.forEach((subsetAttributeKey) => {
            const subsetAttributeValue = clientStudentGroup[subsetAttributeKey];

            if (typeof subsetAttributeValue !== 'undefined') {
              entry[subsetAttributeKey] = subsetAttributeValue;
            }
          });

          if (clientStudentGroup.school && !clientStudentGroup.parent) {
            entry.parentId = `${clientStudentGroup.school}`;
            entry.parentType = 'school';
            groupManagement.associationHash.year[entryId] = true;
          } else if (clientStudentGroup.school && clientStudentGroup.parent) {
            delete entry.school;
            entry.parentId = `${clientStudentGroup.parent}`;
            entry.parentType = 'group';
            if (clientStudentGroup.subjectGroup) {
              groupManagement.associationHash.subject[entryId] = true;
            } else {
              groupManagement.associationHash.class[entryId] = true;
            }
          } else if (clientStudentGroup.parent) {
            entry.parentId = `${clientStudentGroup.parent}`;
            entry.parentType = 'group';
            if (clientStudentGroup.subjectGroup) {
              groupManagement.associationHash.subject[entryId] = true;
            } else {
              groupManagement.associationHash.class[entryId] = true;
            }
          } else if (clientStudentGroup.customGroup) {
            groupManagement.associationHash.custom[entryId] = true;
          }
        });
      }

      return groupManagement;
    },
    async populateGroupTable(entryHash, associationHash) {
      this.groupsLoading = true;
      const requestParam = {
        'vendor-sid': this.$store.state.common.apiParam['vendor-sid'],
        'oauth-signature': this.$store.state.common.apiParam['oauth-signature']
      };

      if (!this.$store.state.common.userClientSid) {
        this.groupsLoading = false;
        return;
      }
      const getClientsArgHash = {
        sid: this.$store.state.common.userClientSid,
        requestParam,
        store: this.$store
      };
      const cbGetClientsResponse = await this.$app.stbApiAdapter.getClients(getClientsArgHash);
      if (!cbGetClientsResponse.error) {
        const schoolTypeData = JSON.parse(
          JSON.stringify(cbGetClientsResponse.data[0].metadata.schoolType)
        );
        this.schoolTypes = [];
        if (schoolTypeData) {
          Object.keys(schoolTypeData).forEach((schoolKey) => {
            this.schoolTypes.push({
              id: schoolKey,
              name: schoolTypeData[schoolKey]
            });
          });
        }
        const client = cbGetClientsResponse.data[0];
        const vendors = client[dbEntityRelation.VENDOR_LIST];
        const activeStudentManagementVendorSid = client.metadata?.activeStudentManagementVendorSid;
        if (activeStudentManagementVendorSid) {
          const vendor = vendors.find((v) => v.sid === `${activeStudentManagementVendorSid}`);
          if (vendor?.join_metadata?.studentManagement) {
            this.isReadFromCache = vendor.join_metadata.studentManagement.isStudentGroupListCached;
            this.studentManagementTestConnectionAt =
              vendor.join_metadata.studentManagement.testConnectionAt || '';
            this.studentManagementTestConnectionFailedStatus =
              vendor.join_metadata.studentManagement.testConnectionFailedStatus || '';
          }
        }
        this.showStudentManagementTestConnectionBtn =
          !!client.computed?.showStudentManagementTestConnectionBtn;
      }

      this.groupList = [];
      Object.keys(entryHash).forEach((entryHashKey) => {
        const entryHashValue = entryHash[entryHashKey];
        const clientStudentGroup = {
          id: entryHashKey,
          group: entryHashValue.name
        };

        this.clientStudentGroupSubsetAttributeKeys.forEach((subsetAttributeKey) => {
          const subsetAttributeValue = entryHashValue[subsetAttributeKey];

          if (typeof subsetAttributeValue !== 'undefined') {
            clientStudentGroup[subsetAttributeKey] = subsetAttributeValue;
          }
        });

        switch (entryHashValue.parentType) {
          case 'group':
            clientStudentGroup.parent = `${entryHashValue.parentId}`;
            break;

          case 'school':
            clientStudentGroup.school = `${entryHashValue.parentId}`;
            break;

          default:
            break;
        }

        if (associationHash.custom && associationHash.custom[entryHashKey]) {
          clientStudentGroup.customGroup = true;
        }

        if (associationHash.subject && associationHash.subject[entryHashKey]) {
          clientStudentGroup.subjectGroup = true;
        }

        this.groupList.push(clientStudentGroup);
      });
      this.groupList = this.$app.lodash.sortBy(
        JSON.parse(JSON.stringify(this.groupList)),
        (o) => o.group
      );
      this.groupList = this.$app.lodash.cloneDeep(this.groupList);
      this.originalGroupList = this.$app.lodash.cloneDeep(this.groupList);
      this.savedGroupManagement = {
        entryHash,
        associationHash
      };
      this.$forceUpdate();
      this.groupsLoading = false;
    },
    getStudentGroupFilterData(paginationOptions) {
      const searchQuery = {
        page: paginationOptions.page,
        length: paginationOptions.itemsPerPage
      };
      if (paginationOptions.sortBy.length !== 0 && paginationOptions.sortBy[0] !== '') {
        searchQuery.order = paginationOptions.sortBy[0].key;
        if (paginationOptions.sortBy[0].order) {
          searchQuery.direction = paginationOptions.sortBy[0].order.toUpperCase();
        }
      }

      if (this.groupList.length === 0) {
        delete searchQuery.page;
        delete searchQuery.length;
        delete searchQuery.direction;
        delete searchQuery.order;
      }

      if (this.searchTerm && String(this.searchTerm).trim().length !== 0) {
        searchQuery.s = String(this.searchTerm).trim().toLocaleLowerCase();
      }

      if (paginationOptions.sortBy[0] === 'group') {
        if (paginationOptions.sortBy[0].order === 'desc') {
          this.groupList = this.$app.lodash
            .sortBy(JSON.parse(JSON.stringify(this.groupList)), (o) => o.group)
            .reverse();
        } else {
          this.groupList = this.$app.lodash.sortBy(
            JSON.parse(JSON.stringify(this.groupList)),
            (o) => o.group
          );
        }
      } else {
        this.groupList = this.$app.lodash.sortBy(
          JSON.parse(JSON.stringify(this.groupList)),
          (o) => o.group
        );
      }
      this.groupList = this.$app.lodash.cloneDeep(this.groupList);
      this.$forceUpdate();
      this.$router.replace({ query: searchQuery }, () => {});
    },
    updateQueryString(query) {
      const searchQuery = {};
      const {
        page: parsedPage,
        length: parsedLength,
        direction: parsedDirection,
        order: parsedSortBy,
        s: parsedSearchGroup
      } = query;

      if (parsedSearchGroup && String(parsedSearchGroup).trim().length !== 0) {
        this.searchTerm = String(parsedSearchGroup).trim().toLowerCase();
        searchQuery.s = this.searchTerm;
        this.groupList = this.originalGroupList.filter((item) =>
          String(item.group).toLowerCase().includes(parsedSearchGroup)
        );
        this.groupList = this.$app.lodash.cloneDeep(this.groupList);
        this.$forceUpdate();
      }

      if (parsedLength === '10' || parsedLength === '30' || parsedLength === '50') {
        this.studentGroupPaginationInfo.itemsPerPage = parseInt(parsedLength, 10);
        searchQuery.length = parseInt(parsedLength, 10);
      } else {
        this.studentGroupPaginationInfo.itemsPerPage = 30;
        searchQuery.length = 30;
      }

      if (!Number.isNaN(parseInt(parsedPage, 10)) && parseInt(parsedPage, 10) > 0) {
        this.studentGroupPaginationInfo.page = parseInt(parsedPage, 10);
        searchQuery.page = parseInt(parsedPage, 10);
      } else {
        this.studentGroupPaginationInfo.page = 1;
        searchQuery.page = 1;
      }
      if (
        studentGroupTableColumn.GROUP.querystringRef === parsedSortBy ||
        studentGroupTableColumn.SCHOOL_TYPE.querystringRef === parsedSortBy ||
        studentGroupTableColumn.PARENT.querystringRef === parsedSortBy ||
        studentGroupTableColumn.SUBJECT_GROUP.querystringRef === parsedSortBy ||
        studentGroupTableColumn.CUSTOM_GROUP.querystringRef === parsedSortBy
      ) {
        this.studentGroupPaginationInfo.sortBy[0] = { key: parsedSortBy };
        searchQuery.order = parsedSortBy;
        if (parsedDirection === 'DESC' || parsedDirection === 'ASC') {
          this.studentGroupPaginationInfo.sortBy[0].order = parsedDirection.toLowerCase();
          searchQuery.direction = parsedDirection;
        } else {
          searchQuery.direction = 'ASC';
        }
      }
      if (this.groupList.length === 0) {
        delete searchQuery.page;
        delete searchQuery.length;
        delete searchQuery.direction;
        delete searchQuery.order;
      }
      this.$router.replace({ query: searchQuery }, () => {});
    },
    filterGroups() {
      this.resetStudentGroupPaginationInfo();
      const { query } = this.$route;
      const searchQuery = this.$app.lodash.cloneDeep(query);
      const parsedSearchTerm = String(this.searchTerm).trim().toLowerCase();
      if (this.searchTerm && parsedSearchTerm.length !== 0) {
        searchQuery.s = parsedSearchTerm;
        this.groupList = this.originalGroupList.filter((item) =>
          String(item.group).toLowerCase().includes(parsedSearchTerm)
        );
        this.groupList = this.$app.lodash.cloneDeep(this.groupList);
        this.$forceUpdate();
        this.updateQueryString(searchQuery);
      } else {
        this.clearSearchInput();
      }
    },
    clearSearchInput() {
      this.searchTerm = '';
      this.groupList = this.$app.lodash.cloneDeep(this.originalGroupList);
      const { query } = this.$route;
      delete query.s;
      const searchQuery = JSON.parse(JSON.stringify(query));
      this.$router.replace({ query: {} }, () => {});
      this.$router.replace({ query: { ...searchQuery } }, () => {});
      this.$forceUpdate();
    },
    resetStudentGroupPaginationInfo() {
      if (this.studentGroupPaginationInfo.itemsPerPage !== 30) {
        this.studentGroupPaginationInfo.itemsPerPage = 30;
      }
      if (this.studentGroupPaginationInfo.page !== 1) {
        this.studentGroupPaginationInfo.page = 1;
      }
      if (this.studentGroupPaginationInfo.sortBy && this.studentGroupPaginationInfo.sortBy[0]) {
        this.studentGroupPaginationInfo.sortBy = [];
      }
    },
    async studentManagementTestConnection() {
      this.studentManagementTestConnectionInProgress = true;

      const result = await this.$app.stbApiAdapter.getClientStudentTestConnection();

      this.studentManagementTestConnectionInProgress = false;

      this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      this.groupSaveGenericDialogDisplayed = true;
      this.isGenericDialogDisplayedReloadAfter = false;

      if (!result.error) {
        this.studentManagementTestConnectionAt = result.data.connectionTestedAt;
        this.studentManagementTestConnectionFailedStatus = '';
        this.isGenericDialogDisplayedAsError = false;
        this.groupSaveGenericDialogMessage = `Successfully connect with student management vendor for school "${result.data.schoolName}".`;
      } else {
        this.studentManagementTestConnectionAt = this.$app.moment().toISOString();
        this.studentManagementTestConnectionFailedStatus = result.message;
        this.isGenericDialogDisplayedAsError = true;
        this.groupSaveGenericDialogMessage = result.message;
      }
    },
    clearApplyParentSelection() {
      this.selectedApplyParentId = null;
      this.$refs.selectGroup.blur();
    },
    clearSchoolTypeSelection(item) {
      // eslint-disable-next-line no-param-reassign
      item.school = null;
    },
    clearParentSelection(item, controlRef) {
      // eslint-disable-next-line no-param-reassign
      item.parent = null;
      if (controlRef) {
        controlRef.onBlur();
      }
    }
  }
};
</script>

<style scoped>
.student-management-panel {
  margin-left: 50px;
  margin-right: 50px;
  margin-top: 30px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: fit-content;
  background-color: #fff;
}

.student-management-panel-student-select {
  margin-top: 10px !important;
  margin-bottom: 5px !important;
}

.student-management-group-search-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  max-height: 100px;
  margin-top: 8px;
}

#student-management-search-input {
  width: 300px;
  padding-left: 4px;
}

.studentGroupId {
  color: gray;
}

.row-select-checkbox {
  padding-left: 8px !important;
}

.generic-error-message {
  color: rgb(var(--v-theme-error));
  text-align: left;
}

.checkbox-options {
  max-width: 150px;
  padding-bottom: 8px;
}

.student-management-panel-parent-select {
  width: 300px;
  height: 35px;
  margin-top: 6px !important;
  float: left;
}

.student-management-panel-parent {
  color: #c80063 !important;
}

.student-management-panel :deep(.table-header) {
  font-weight: 700 !important;
  font-size: 12px !important;
  line-height: 18px !important;
  white-space: nowrap;
}

.student-management-panel :deep(thead > tr > th > .mdi-checkbox-marked) {
  color: red !important;
}
</style>
